import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { environment } from 'src/environments/environment';

const BASE = `${environment.apiNest}/marketplace/auth`;
const K_ACCESS = 'mkt_access';
const K_REFRESH = 'mkt_refresh';
const K_USER = 'mkt_user';

export interface MktUser {
  sub: number;
  tipo: 'EMPRESA' | 'CLIENTE' | 'PROFESIONAL';
  idEmpresa: number | null;
  /** Solo para tipo PROFESIONAL: su fila en MktProfesional. */
  idProfesional?: number | null;
  email: string;
  nombre: string | null;
}

/** Auth del marketplace (JWT). Separada de la auth de C# (usersKey). Ver SPEC-016. */
@Injectable({ providedIn: 'root' })
export class MarketplaceAuthService {
  /** Usuario actual (signal para la UI). */
  readonly user = signal<MktUser | null>(this.readUser());

  constructor(private http: HttpClient) {}

  get accessToken(): string | null {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(K_ACCESS) : null;
  }
  get refreshToken(): string | null {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(K_REFRESH) : null;
  }
  isLogged(): boolean {
    return !!this.accessToken;
  }
  isEmpresa(): boolean {
    return this.user()?.tipo === 'EMPRESA';
  }

  login(email: string, password: string): Observable<any> {
    return this.http.post<any>(`${BASE}/login`, { email, password }).pipe(tap((r) => this.store(r)));
  }
  registerCliente(dto: { email: string; password: string; nombre?: string; telefono?: string }): Observable<any> {
    return this.http.post<any>(`${BASE}/register`, dto).pipe(tap((r) => this.store(r)));
  }
  refresh(): Observable<any> {
    return this.http.post<any>(`${BASE}/refresh`, { refreshToken: this.refreshToken }).pipe(tap((r) => this.store(r)));
  }

  /** Guarda tokens+user (también usado tras el onboarding con auto-login). */
  store(r: { accessToken?: string; refreshToken?: string; user?: MktUser }): void {
    if (!r?.accessToken) return;
    localStorage.setItem(K_ACCESS, r.accessToken);
    if (r.refreshToken) localStorage.setItem(K_REFRESH, r.refreshToken);
    if (r.user) {
      localStorage.setItem(K_USER, JSON.stringify(r.user));
      this.user.set(r.user);
    }
  }

  logout(): void {
    const rt = this.refreshToken;
    if (rt) {
      // revoca el refresh en el backend (fire-and-forget)
      this.http.post(`${BASE}/logout`, { refreshToken: rt }).subscribe({ error: () => {} });
    }
    localStorage.removeItem(K_ACCESS);
    localStorage.removeItem(K_REFRESH);
    localStorage.removeItem(K_USER);
    this.user.set(null);
  }

  private readUser(): MktUser | null {
    try {
      const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(K_USER) : null;
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }
}
